import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Alert, Button, Field } from "@/components/ui";
import { AuthLayout } from "@/pages/Login";

/**
 * Asking for a link to set a new password.
 *
 * The answer is the same whether or not the address has a login, because a
 * different one would let anybody ask whether a given company ships with us.
 * That means this screen cannot confirm anything either — it says what was done
 * with the address, not what was found.
 */
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      const result = await api.publicPost<{ message: string }>("/v1/auth/password/forgot", { email });
      setSent(result.message);
    } catch {
      setError("Could not send that. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Forgotten password" subtitle="We will email you a link to set a new one.">
      {sent ? (
        <div className="space-y-4">
          <Alert tone="good">{sent}</Alert>
          <p className="text-sm text-ink-500">
            Check your spam folder if it has not arrived in a few minutes. The link works once.
          </p>
          <Link className="text-sm font-medium text-brand" to="/login">
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          {error && <Alert>{error}</Alert>}
          <Field
            label="Email"
            type="email"
            name="email"
            required
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@company.co.za"
          />
          <Button type="submit" className="w-full" loading={busy} disabled={!email.includes("@")}>
            Email me a link
          </Button>
          <p className="text-center text-sm text-ink-500">
            <Link className="font-medium text-brand" to="/login">
              Back to sign in
            </Link>
          </p>
        </form>
      )}
    </AuthLayout>
  );
}
